<template>
  <div class="politica-detail">
    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
    >
      <div>
        <h5 class="mb-1">
          Detalhes da Política de Cobrança
          <span v-if="politica">
            #{{ politica.id }}
          </span>
        </h5>

        <small class="text-muted">
          Regras comerciais e período de vigência da política.
        </small>
      </div>

      <div class="d-flex flex-wrap align-items-center gap-2">
        <button
          v-if="podePublicar"
          type="button"
          class="btn btn-success btn-sm"
          :disabled="loading || acaoEmAndamento"
          @click="confirmarPublicacao"
        >
          <span
            v-if="acaoAtual === 'PUBLICAR'"
            class="spinner-border spinner-border-sm me-1"
            role="status"
            aria-hidden="true"
          ></span>

          <i
            v-else
            class="bi bi-check-circle-fill me-1"
          ></i>

          Publicar
        </button>

        <button
          v-if="podeEncerrar"
          type="button"
          class="btn btn-outline-warning btn-sm"
          :disabled="loading || acaoEmAndamento"
          @click="abrirEncerramento"
        >
          <i class="bi bi-calendar-x me-1"></i>
          Encerrar vigência
        </button>

        <button
          v-if="podeCancelar"
          type="button"
          class="btn btn-outline-danger btn-sm"
          :disabled="loading || acaoEmAndamento"
          @click="confirmarCancelamento"
        >
          <span
            v-if="acaoAtual === 'CANCELAR'"
            class="spinner-border spinner-border-sm me-1"
            role="status"
            aria-hidden="true"
          ></span>

          <i
            v-else
            class="bi bi-x-octagon me-1"
          ></i>

          Cancelar
        </button>

        <button
          v-if="podeEditar"
          type="button"
          class="btn btn-warning btn-sm"
          :disabled="loading || acaoEmAndamento"
          @click="editar"
        >
          <i class="bi bi-pencil-fill me-1"></i>
          Editar rascunho
        </button>

        <button
          type="button"
          class="btn btn-secondary btn-sm"
          :disabled="loading || acaoEmAndamento"
          @click="voltar"
        >
          <i class="bi bi-arrow-left me-1"></i>
          Voltar
        </button>
      </div>
    </div>

    <div
      v-if="loading"
      class="card card-body shadow-sm py-5 text-center"
    >
      <div>
        <div
          class="spinner-border spinner-border-sm text-primary"
          role="status"
        ></div>
      </div>

      <div class="mt-2 text-muted">
        Carregando política...
      </div>
    </div>

    <div
      v-else-if="errorMessage"
      class="alert alert-danger"
      role="alert"
    >
      {{ errorMessage }}
    </div>

    <template v-else-if="politica">
      <div
        v-if="successMessage"
        class="alert alert-success"
        role="status"
      >
        {{ successMessage }}
      </div>

      <div
        v-if="actionErrorMessage"
        class="alert alert-danger"
        role="alert"
      >
        {{ actionErrorMessage }}

        <button
          v-if="concurrencyConflict"
          type="button"
          class="btn btn-outline-danger btn-sm ms-2"
          :disabled="acaoEmAndamento"
          @click="recarregarAposConflito"
        >
          Recarregar política
        </button>
      </div>

      <div
        v-if="encerramentoAberto"
        class="card shadow-sm mb-3 border-warning"
      >
        <div class="card-body">
          <h6 class="mb-2">
            Encerrar vigência
          </h6>

          <p class="small text-muted mb-2">
            Informe o último dia em que esta política continuará válida.
            A data final é inclusiva e o backend validará a data de negócio.
          </p>

          <div class="row g-2 align-items-end">
            <div class="col-md-4">
              <label
                for="vigencia-fim-encerramento"
                class="form-label"
              >
                Fim da vigência
              </label>

              <input
                id="vigencia-fim-encerramento"
                v-model="vigenciaFimEncerramento"
                type="date"
                class="form-control form-control-sm"
                :disabled="acaoEmAndamento"
              />
            </div>

            <div class="col-md-auto">
              <button
                type="button"
                class="btn btn-warning btn-sm me-2"
                :disabled="
                  acaoEmAndamento ||
                  !vigenciaFimEncerramento
                "
                @click="confirmarEncerramento"
              >
                <span
                  v-if="acaoAtual === 'ENCERRAR'"
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>

                Confirmar encerramento
              </button>

              <button
                type="button"
                class="btn btn-secondary btn-sm"
                :disabled="acaoEmAndamento"
                @click="fecharEncerramento"
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      </div>

      <FormSection title="Identificação e vigência">
        <div class="details-grid">
          <div class="detail-item detail-wide">
            <span class="detail-label">
              Nome
            </span>

            <strong>
              {{ politica.nome || '—' }}
            </strong>
          </div>

          <div class="detail-item">
            <span class="detail-label">
              Situação efetiva
            </span>

            <span
              class="status-badge"
              :class="statusClass(politica.situacao_efetiva)"
            >
              {{ statusLabel(politica.situacao_efetiva) }}
            </span>
          </div>

          <DetailValue
            label="Situação registrada"
            :value="statusLabel(politica.situacao)"
          />

          <DetailValue
            label="Início da vigência"
            :value="formatDate(politica.vigencia_inicio)"
          />

          <DetailValue
            label="Fim da vigência"
            :value="politica.vigencia_fim
              ? formatDate(politica.vigencia_fim)
              : 'Sem término definido'"
          />
        </div>
      </FormSection>

      <FormSection title="Dias de vencimento">
        <div
          v-if="diasOrdenados.length"
          class="dias-grid"
        >
          <div
            v-for="dia in diasOrdenados"
            :key="dia.dia"
            class="dia-card"
            :class="{ 'dia-padrao': dia.padrao }"
          >
            <strong>Dia {{ dia.dia }}</strong>

            <span
              v-if="dia.padrao"
              class="badge bg-primary ms-2"
            >
              Padrão
            </span>
          </div>
        </div>

        <div
          v-else
          class="text-muted"
        >
          Nenhum dia de vencimento configurado.
        </div>
      </FormSection>

      <FormSection title="Pró-rata">
        <div class="details-grid">
          <DetailValue
            label="Habilitado"
            :value="yesNo(politica.prorata_habilitado)"
          />

          <DetailValue
            label="Modo"
            :value="prorataLabel(politica)"
          />
        </div>
      </FormSection>

      <FormSection title="Desconto por antecipação">
        <div class="details-grid">
          <DetailValue
            label="Habilitado"
            :value="yesNo(
              politica.desconto_antecipacao_habilitado
            )"
          />

          <DetailValue
            label="Percentual"
            :value="
              politica.desconto_antecipacao_habilitado
                ? percentual(
                    politica.desconto_antecipacao_percentual
                  )
                : '—'
            "
          />

          <DetailValue
            label="Antecedência"
            :value="
              politica.desconto_antecipacao_habilitado
                ? diasAntecedencia(
                    politica.desconto_antecipacao_dias
                  )
                : '—'
            "
          />
        </div>
      </FormSection>

      <FormSection title="Multa por atraso">
        <div class="details-grid">
          <DetailValue
            label="Habilitada"
            :value="yesNo(politica.multa_habilitada)"
          />

          <DetailValue
            label="Percentual"
            :value="
              politica.multa_habilitada
                ? percentual(politica.multa_percentual)
                : '—'
            "
          />
        </div>
      </FormSection>

      <FormSection v-if="politica.sicredi_juros_percentual_dia != null" title="Mora percentual diária — SICREDI">
        <p>{{ politica.sicredi_juros_percentual_dia }}% ao dia. Início e arredondamento pendentes de confirmação bancária.</p>
      </FormSection>
      <FormSection v-if="politica.sicredi_juros_valor_dia != null" title="Juros diários — SICREDI">
        <p>R$ {{ politica.sicredi_juros_valor_dia }} por dia.</p>
        <p class="text-muted mt-2">Não é possível definir a data de início dos juros nesta integração.</p>
      </FormSection>
      <FormSection title="Juros mensais">
        <div class="details-grid">
          <DetailValue
            label="Habilitados"
            :value="yesNo(politica.juros_habilitado)"
          />

          <DetailValue
            label="Percentual mensal"
            :value="
              politica.juros_habilitado
                ? percentual(
                    politica.juros_percentual_mes,
                    ' ao mês'
                  )
                : '—'
            "
          />
        </div>
      </FormSection>

      <FormSection title="Auditoria">
        <div class="details-grid">
          <DetailValue
            label="Criada em"
            :value="formatDateTime(politica.criado_em)"
          />

          <DetailValue
            label="Criada por"
            :value="idLabel(politica.criado_por)"
          />

          <DetailValue
            label="Atualizada em"
            :value="formatDateTime(politica.atualizado_em)"
          />

          <DetailValue
            label="Atualizada por"
            :value="idLabel(politica.atualizado_por)"
          />

          <DetailValue
            label="Publicada em"
            :value="formatDateTime(politica.publicado_em)"
          />

          <DetailValue
            label="Publicada por"
            :value="idLabel(politica.publicado_por)"
          />

          <DetailValue
            label="Cancelada em"
            :value="formatDateTime(politica.cancelado_em)"
          />

          <DetailValue
            label="Cancelada por"
            :value="idLabel(politica.cancelado_por)"
          />
        </div>
      </FormSection>
    </template>
  </div>
</template>

<script setup>
import {
  computed,
  defineComponent,
  h,
  onMounted,
  ref
} from 'vue';

import {
  useRoute,
  useRouter
} from 'vue-router';

import FormSection
  from '@/components/Layout/FormSection.vue';

import {
  usePermissions
} from '@/composables/usePermissions';

import politicasCobrancasApi
  from '@/services/politicasCobrancas.api';

const DetailValue =
  defineComponent({
    name:
      'PoliticaDetailValue',

    props: {
      label: {
        type: String,
        required: true
      },

      value: {
        type: [
          String,
          Number
        ],
        default: '—'
      }
    },

    setup(props) {
      return () =>
        h(
          'div',
          {
            class:
              'detail-item'
          },
          [
            h(
              'span',
              {
                class:
                  'detail-label'
              },
              props.label
            ),

            h(
              'span',
              {},
              props.value ===
                null ||
              props.value ===
                undefined ||
              props.value ===
                ''
                ? '—'
                : String(
                    props.value
                  )
            )
          ]
        );
    }
  });

const route =
  useRoute();

const router =
  useRouter();

const {
  fetchPermissions,
  hasPermission
} = usePermissions();

const politica =
  ref(null);

const loading =
  ref(true);

const errorMessage =
  ref('');

const actionErrorMessage =
  ref('');

const successMessage =
  ref('');

const concurrencyConflict =
  ref(false);

const acaoEmAndamento =
  ref(false);

const acaoAtual =
  ref('');

const encerramentoAberto =
  ref(false);

const vigenciaFimEncerramento =
  ref('');

const podeConfigurar =
  computed(() =>
    hasPermission(
      'financeiro_cobrancas_configurar'
    )
  );

const podeEditar =
  computed(() =>
    podeConfigurar.value &&
    politica.value
      ?.situacao ===
        'RASCUNHO'
  );

const podePublicar =
  computed(() =>
    podeConfigurar.value &&
    politica.value
      ?.situacao ===
        'RASCUNHO'
  );

const podeEncerrar =
  computed(() =>
    podeConfigurar.value &&
    politica.value
      ?.situacao ===
        'PUBLICADA' &&
    politica.value
      ?.situacao_efetiva ===
        'VIGENTE' &&
    !politica.value
      ?.vigencia_fim
  );

const podeCancelar =
  computed(() =>
    podeConfigurar.value &&
    (
      politica.value
        ?.situacao ===
          'RASCUNHO' ||
      (
        politica.value
          ?.situacao ===
            'PUBLICADA' &&
        politica.value
          ?.situacao_efetiva ===
            'AGENDADA'
      )
    )
  );

const diasOrdenados =
  computed(() => {
    if (
      !Array.isArray(
        politica.value
          ?.dias_vencimento
      )
    ) {
      return [];
    }

    return [
      ...politica.value
        .dias_vencimento
    ].sort(
      (a, b) =>
        Number(a?.dia) -
        Number(b?.dia)
    );
  });

function apiErrorMessage(
  error,
  fallback
) {
  return (
    error?.response?.data?.message ||
    fallback
  );
}

function normalizeRouteId(
  value
) {
  const id =
    Number(value);

  if (
    !Number.isSafeInteger(id) ||
    id <= 0
  ) {
    return null;
  }

  return id;
}

function formatDate(
  value
) {
  if (
    typeof value !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(
      value
    )
  ) {
    return '—';
  }

  const [
    year,
    month,
    day
  ] = value.split('-');

  return `${day}/${month}/${year}`;
}

function formatDateTime(
  value
) {
  if (
    typeof value !== 'string'
  ) {
    return '—';
  }

  const match =
    value.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/
    );

  if (!match) {
    return value || '—';
  }

  const [
    ,
    year,
    month,
    day,
    hour,
    minute,
    second
  ] = match;

  return (
    `${day}/${month}/${year} ` +
    `${hour}:${minute}:${second}`
  );
}

function formatDecimal(
  value
) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '—';
  }

  const raw =
    String(value).trim();

  if (
    !/^\d+(?:\.\d+)?$/.test(
      raw
    )
  ) {
    return raw;
  }

  const [
    integer,
    decimals = ''
  ] = raw.split('.');

  const trimmed =
    decimals.replace(
      /0+$/,
      ''
    );

  return trimmed
    ? `${integer},${trimmed}`
    : integer;
}

function percentual(
  value,
  suffix = ''
) {
  const formatted =
    formatDecimal(value);

  if (formatted === '—') {
    return formatted;
  }

  return `${formatted}%${suffix}`;
}

function yesNo(
  value
) {
  return value
    ? 'Sim'
    : 'Não';
}

function idLabel(
  value
) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '—';
  }

  return `#${value}`;
}

function diasAntecedencia(
  value
) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '—';
  }

  const numero =
    String(value);

  return (
    `${numero} ` +
    (
      numero === '1'
        ? 'dia antes'
        : 'dias antes'
    )
  );
}

function prorataLabel(
  item
) {
  if (
    !item?.prorata_habilitado
  ) {
    return 'Não aplicável';
  }

  if (
    item.prorata_modo ===
      'PRIMEIRA_PARCELA'
  ) {
    return (
      'Cobrar o pró-rata em parcela inicial separada'
    );
  }

  if (
    item.prorata_modo ===
      'ACUMULAR_PROXIMA_PARCELA'
  ) {
    return (
      'Acumular o pró-rata na próxima parcela regular'
    );
  }

  return 'Modo não identificado';
}

function statusLabel(
  status
) {
  const labels = {
    RASCUNHO:
      'Rascunho',

    PUBLICADA:
      'Publicada',

    AGENDADA:
      'Agendada',

    VIGENTE:
      'Vigente',

    ENCERRADA:
      'Encerrada',

    CANCELADA:
      'Cancelada'
  };

  return (
    labels[
      String(
        status || ''
      ).toUpperCase()
    ] ||
    status ||
    '—'
  );
}

function statusClass(
  status
) {
  const classes = {
    RASCUNHO:
      'status-secondary',

    PUBLICADA:
      'status-primary',

    AGENDADA:
      'status-info',

    VIGENTE:
      'status-success',

    ENCERRADA:
      'status-dark',

    CANCELADA:
      'status-danger'
  };

  return (
    classes[
      String(
        status || ''
      ).toUpperCase()
    ] ||
    'status-secondary'
  );
}

async function carregarPolitica() {
  const id =
    normalizeRouteId(
      route.params.id
    );

  if (!id) {
    politica.value =
      null;

    errorMessage.value =
      'Identificador da política de cobrança inválido.';

    loading.value =
      false;

    return;
  }

  if (
    !hasPermission(
      'financeiro_cobrancas_visualizar'
    )
  ) {
    politica.value =
      null;

    errorMessage.value =
      'Você não possui permissão para visualizar políticas de cobrança.';

    loading.value =
      false;

    return;
  }

  loading.value =
    true;

  errorMessage.value =
    '';

  actionErrorMessage.value =
    '';

  successMessage.value =
    '';

  concurrencyConflict.value =
    false;

  try {
    const result =
      await politicasCobrancasApi
        .detalhar(id);

    if (
      !result?.politica ||
      typeof result.politica !==
        'object'
    ) {
      throw new Error(
        'Resposta inválida do serviço de políticas.'
      );
    }

    politica.value =
      result.politica;
  } catch (error) {
    console.error(
      'Erro ao carregar política de cobrança:',
      error
    );

    politica.value =
      null;

    errorMessage.value =
      apiErrorMessage(
        error,
        'Não foi possível carregar a política de cobrança.'
      );
  } finally {
    loading.value =
      false;
  }
}

function tokenAtual() {
  return String(
    politica.value
      ?.atualizado_em_token ??
    ''
  );
}

function limparFeedbackAcao() {
  actionErrorMessage.value =
    '';

  successMessage.value =
    '';

  concurrencyConflict.value =
    false;
}

function aplicarResultadoTransicao(
  result,
  successText
) {
  if (
    !result?.politica ||
    typeof result.politica !==
      'object'
  ) {
    throw new Error(
      'Resposta inválida ao alterar o estado da política.'
    );
  }

  politica.value =
    result.politica;

  successMessage.value =
    successText;

  encerramentoAberto.value =
    false;

  vigenciaFimEncerramento.value =
    '';
}

function tratarErroTransicao(
  error,
  fallback
) {
  console.error(
    'Erro ao alterar estado da política de cobrança:',
    error
  );

  if (
    error?.response?.status ===
      409
  ) {
    concurrencyConflict.value =
      true;

    actionErrorMessage.value =
      'A política foi alterada por outro usuário ou seu estado mudou. Recarregue os dados antes de tentar novamente.';

    return;
  }

  actionErrorMessage.value =
    apiErrorMessage(
      error,
      fallback
    );
}

async function confirmarPublicacao() {
  if (
    !podePublicar.value ||
    acaoEmAndamento.value
  ) {
    return;
  }

  const confirmed =
    window.confirm(
      'Publicar esta política de cobrança? Depois de publicada, seus termos comerciais não poderão ser editados.'
    );

  if (!confirmed) {
    return;
  }

  limparFeedbackAcao();

  acaoEmAndamento.value =
    true;

  acaoAtual.value =
    'PUBLICAR';

  try {
    const result =
      await politicasCobrancasApi
        .publicar(
          politica.value.id,
          tokenAtual()
        );

    aplicarResultadoTransicao(
      result,
      'Política publicada com sucesso.'
    );
  } catch (error) {
    tratarErroTransicao(
      error,
      'Não foi possível publicar a política de cobrança.'
    );
  } finally {
    acaoEmAndamento.value =
      false;

    acaoAtual.value =
      '';
  }
}

function abrirEncerramento() {
  if (
    !podeEncerrar.value ||
    acaoEmAndamento.value
  ) {
    return;
  }

  limparFeedbackAcao();

  vigenciaFimEncerramento.value =
    '';

  encerramentoAberto.value =
    true;
}

function fecharEncerramento() {
  if (
    acaoEmAndamento.value
  ) {
    return;
  }

  encerramentoAberto.value =
    false;

  vigenciaFimEncerramento.value =
    '';
}

async function confirmarEncerramento() {
  if (
    !podeEncerrar.value ||
    acaoEmAndamento.value
  ) {
    return;
  }

  const vigenciaFim =
    String(
      vigenciaFimEncerramento.value ??
      ''
    ).trim();

  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(
      vigenciaFim
    )
  ) {
    actionErrorMessage.value =
      'Informe uma data válida para o fim da vigência.';

    return;
  }

  const confirmed =
    window.confirm(
      `Encerrar a vigência desta política em ${vigenciaFim}? A data final será considerada inclusiva.`
    );

  if (!confirmed) {
    return;
  }

  limparFeedbackAcao();

  acaoEmAndamento.value =
    true;

  acaoAtual.value =
    'ENCERRAR';

  try {
    const result =
      await politicasCobrancasApi
        .encerrar(
          politica.value.id,
          vigenciaFim,
          tokenAtual()
        );

    aplicarResultadoTransicao(
      result,
      'Fim da vigência definido com sucesso.'
    );
  } catch (error) {
    tratarErroTransicao(
      error,
      'Não foi possível encerrar a vigência da política.'
    );
  } finally {
    acaoEmAndamento.value =
      false;

    acaoAtual.value =
      '';
  }
}

async function confirmarCancelamento() {
  if (
    !podeCancelar.value ||
    acaoEmAndamento.value
  ) {
    return;
  }

  const confirmed =
    window.confirm(
      politica.value?.situacao ===
        'RASCUNHO'
        ? 'Cancelar este rascunho de política? Esta operação encerrará definitivamente o rascunho.'
        : 'Cancelar esta política agendada antes de sua entrada em vigor?'
    );

  if (!confirmed) {
    return;
  }

  limparFeedbackAcao();

  acaoEmAndamento.value =
    true;

  acaoAtual.value =
    'CANCELAR';

  try {
    const result =
      await politicasCobrancasApi
        .cancelar(
          politica.value.id,
          tokenAtual()
        );

    aplicarResultadoTransicao(
      result,
      'Política cancelada com sucesso.'
    );
  } catch (error) {
    tratarErroTransicao(
      error,
      'Não foi possível cancelar a política de cobrança.'
    );
  } finally {
    acaoEmAndamento.value =
      false;

    acaoAtual.value =
      '';
  }
}

async function recarregarAposConflito() {
  if (
    acaoEmAndamento.value
  ) {
    return;
  }

  limparFeedbackAcao();

  encerramentoAberto.value =
    false;

  vigenciaFimEncerramento.value =
    '';

  await carregarPolitica();
}

function editar() {
  if (
    !politica.value?.id ||
    !podeEditar.value
  ) {
    return;
  }

  router.push({
    name:
      'EditarPoliticaCobranca',

    params: {
      id:
        politica.value.id
    }
  });
}

function voltar() {
  router.push({
    name:
      'ListarPoliticasCobrancas'
  });
}

onMounted(
  async () => {
    await fetchPermissions();

    await carregarPolitica();
  }
);
</script>

<style scoped>
.politica-detail {
  width: 100%;
  font-size: 0.875rem;
}

.politica-detail :deep(.form-section) {
  padding: 1.25rem;
  margin-bottom: 1rem;
  background: #fff;
  border-color: #dee2e6;
}
.politica-detail :deep(.form-section-title) { font-size: 1rem; font-weight: 500; }
.politica-detail :deep(.form-section-content) { line-height: 1.5; }

.details-grid {
  display: grid;
  grid-template-columns:
    repeat(
      4,
      minmax(0, 1fr)
    );
  gap: 0.85rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.detail-wide {
  grid-column: span 2;
}

.detail-label {
  color: #6c757d;
  font-size: 0.8125rem;
  margin-bottom: 0.15rem;
}

.status-badge {
  align-self: flex-start;
  display: inline-block;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-secondary {
  background: #e9ecef;
  color: #495057;
}

.status-primary {
  background: #cfe2ff;
  color: #084298;
}

.status-info {
  background: #cff4fc;
  color: #055160;
}

.status-success {
  background: #d1e7dd;
  color: #0f5132;
}

.status-dark {
  background: #dee2e6;
  color: #212529;
}

.status-danger {
  background: #f8d7da;
  color: #842029;
}

.dias-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.dia-card {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #fff;
  padding: 0.4rem 0.6rem;
}

.dia-padrao {
  border-color: #0d6efd;
  background: #f0f6ff;
}

@media (max-width: 1100px) {
  .details-grid {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }
}

@media (max-width: 700px) {
  .details-grid {
    grid-template-columns: 1fr;
  }

  .detail-wide {
    grid-column: auto;
  }
}
</style>
